import { apiClient } from '@/shared/api';
import type {
  BugReport,
  ConvertBugReportToQuestRequest,
  BugReportEvent,
  BugReportMonthlyStats,
  BugReportProcessorStats,
  BugReportVoteSummary,
  BugReportHotspot,
  PaginatedResponse,
} from '@aegislab/shared';
import type {
  BugReportFilters,
  CreateBugReportInput,
  UpdateBugReportInput,
  EditBugReportInput,
} from '../model/types';

export const fetchBugReports = async (
  filters?: BugReportFilters
): Promise<PaginatedResponse<BugReport>> => {
  const response = await apiClient.get<PaginatedResponse<BugReport>>('/bug-reports', {
    params: filters,
  });
  return response.data;
};

export const fetchBugReport = async (id: string): Promise<BugReport> => {
  const response = await apiClient.get<BugReport>(`/bug-reports/${id}`);
  return response.data;
};

export const fetchBugReportEvents = async (
  id: string,
  cursor?: string
): Promise<PaginatedResponse<BugReportEvent>> => {
  const response = await apiClient.get<PaginatedResponse<BugReportEvent>>(
    `/bug-reports/${id}/events`,
    { params: cursor ? { cursor } : undefined }
  );
  return response.data;
};

export const fetchBugReportSuggestions = async (pageContext: string): Promise<BugReport[]> => {
  const response = await apiClient.get<BugReport[]>('/bug-reports/suggestions', {
    params: { pageContext },
  });
  return response.data;
};

export const fetchHotspots = async (): Promise<BugReportHotspot[]> => {
  const response = await apiClient.get<BugReportHotspot[]>('/bug-reports/hotspots');
  return response.data;
};

export const fetchBugReportMonthlyStats = async (
  months?: number
): Promise<BugReportMonthlyStats> => {
  const response = await apiClient.get<BugReportMonthlyStats>('/bug-reports/stats/monthly', {
    params: months ? { months } : undefined,
  });
  return response.data;
};

export const fetchBugReportProcessorStats = async (
  months?: number
): Promise<BugReportProcessorStats> => {
  const response = await apiClient.get<BugReportProcessorStats>(
    '/bug-reports/stats/monthly/processor',
    {
      params: months ? { months } : undefined,
    }
  );
  return response.data;
};

export const fetchAttachmentUrl = async (
  bugReportId: string,
  path: string
): Promise<{ signedUrl: string }> => {
  const response = await apiClient.get<{ signedUrl: string }>(
    `/bug-reports/${bugReportId}/attachment-url`,
    { params: { path } }
  );
  return response.data;
};

export const createBugReport = async (data: CreateBugReportInput): Promise<BugReport> => {
  const response = await apiClient.post<BugReport>('/bug-reports', data);
  return response.data;
};

export const updateBugReport = async (
  id: string,
  data: UpdateBugReportInput
): Promise<BugReport> => {
  const response = await apiClient.patch<BugReport>(`/bug-reports/${id}`, data);
  return response.data;
};

/**
 * 제보를 퀘스트로 전환한다. 본문은 퀘스트 생성 계약 그대로이되 creatorId 는 서버가
 * JWT 로 채우므로 보내지 않는다. 응답은 questId 가 채워진 제보다.
 */
export const convertBugReportToQuest = async (
  id: string,
  data: ConvertBugReportToQuestRequest
): Promise<BugReport> => {
  const response = await apiClient.post<BugReport>(`/bug-reports/${id}/quest`, data);
  return response.data;
};

export const addAffectedUser = async (id: string): Promise<void> => {
  await apiClient.post(`/bug-reports/${id}/affect`);
};

export const verifyResolution = async (id: string, verified: boolean): Promise<BugReport> => {
  const response = await apiClient.post<BugReport>(`/bug-reports/${id}/verify`, {
    verified,
  });
  return response.data;
};

export const editBugReport = async (id: string, data: EditBugReportInput): Promise<BugReport> => {
  const response = await apiClient.patch<BugReport>(`/bug-reports/${id}/edit`, data);
  return response.data;
};

export const createBugReportEvent = async (
  id: string,
  content: string
): Promise<BugReportEvent> => {
  const response = await apiClient.post<BugReportEvent>(`/bug-reports/${id}/events`, { content });
  return response.data;
};

// ── Votes (기능요청 지지 투표) — 본문 없는 POST/DELETE, 응답은 요약 ──

export const fetchBugReportVotes = async (id: string): Promise<BugReportVoteSummary> => {
  const response = await apiClient.get<BugReportVoteSummary>(`/bug-reports/${id}/votes`);
  return response.data;
};

export const castBugReportVote = async (id: string): Promise<BugReportVoteSummary> => {
  const response = await apiClient.post<BugReportVoteSummary>(`/bug-reports/${id}/votes`);
  return response.data;
};

export const retractBugReportVote = async (id: string): Promise<BugReportVoteSummary> => {
  const response = await apiClient.delete<BugReportVoteSummary>(`/bug-reports/${id}/votes`);
  return response.data;
};

export const bugReportApi = {
  fetchBugReports,
  fetchBugReport,
  fetchBugReportEvents,
  fetchBugReportSuggestions,
  fetchHotspots,
  fetchBugReportMonthlyStats,
  fetchBugReportProcessorStats,
  fetchAttachmentUrl,
  createBugReport,
  updateBugReport,
  convertBugReportToQuest,
  editBugReport,
  addAffectedUser,
  verifyResolution,
  createBugReportEvent,
  fetchBugReportVotes,
  castBugReportVote,
  retractBugReportVote,
};
